import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Scissors, Calendar, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import AddSurgeryDialog from './AddSurgeryDialog';

const SurgeriesTab = () => {
  const [surgeries, setSurgeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    fetchSurgeries();
  }, []);

  const fetchSurgeries = async () => {
    try {
      const response = await API.get('/medical/surgeries');
      setSurgeries(response.data);
    } catch (error) {
      console.error('Failed to fetch surgeries:', error);
      toast.error('Failed to load surgeries');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (surgeryData) => {
    try {
      await API.post('/medical/surgeries', surgeryData);
      toast.success('Surgery added');
      fetchSurgeries();
      setShowAdd(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add surgery');
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/medical/surgeries/${id}`);
      toast.success('Surgery deleted');
      fetchSurgeries();
    } catch (error) {
      toast.error('Failed to delete surgery');
    }
  };

  if (loading) {
    return <div className="space-y-4">{[1, 2].map((i) => <Skeleton key={i} className="h-32 w-full" />)}</div>;
  }

  if (surgeries.length === 0) {
    return (
      <>
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-[#B8E6D5] rounded-full flex items-center justify-center mb-4">
            <Scissors className="h-8 w-8 text-gray-700" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No surgeries recorded</h3>
          <p className="text-gray-600 mb-4">Track surgical history and procedures</p>
          <Button onClick={() => setShowAdd(true)} className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900">
            <Plus className="h-4 w-4 mr-2" />
            Add Surgery
          </Button>
        </div>
        <AddSurgeryDialog open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />
      </>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Surgical History</h3>
        <Button onClick={() => setShowAdd(true)} size="sm" className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900">
          <Plus className="h-4 w-4 mr-2" />
          Add Surgery
        </Button>
      </div>

      <div className="space-y-4">
        {surgeries.map((surgery) => (
          <div key={surgery.id} className="border border-gray-200 rounded-lg p-5 bg-white hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900 text-lg">{surgery.name}</h4>
                <div className="flex items-center text-sm text-gray-600 mt-1">
                  <Calendar className="h-4 w-4 mr-1" />
                  {surgery.date}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-3">
              <div>
                <span className="text-gray-600 font-medium">Reason:</span>
                <p className="text-gray-900">{surgery.reason}</p>
              </div>
              {surgery.surgeon && (
                <div>
                  <span className="text-gray-600 font-medium">Surgeon:</span>
                  <p className="text-gray-900">{surgery.surgeon}</p>
                </div>
              )}
              {surgery.facility && (
                <div>
                  <span className="text-gray-600 font-medium">Facility:</span>
                  <p className="text-gray-900">{surgery.facility}</p>
                </div>
              )}
            </div>

            {surgery.complications && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-3">
                <p className="text-sm font-medium text-yellow-900">Complications:</p>
                <p className="text-sm text-yellow-700">{surgery.complications}</p>
              </div>
            )}

            {surgery.care_instructions && (
              <div className="p-3 bg-blue-50 rounded-lg mb-3">
                <p className="text-sm font-medium text-blue-900">Post-Surgery Care:</p>
                <p className="text-sm text-blue-700">{surgery.care_instructions}</p>
              </div>
            )}

            {surgery.document_images && surgery.document_images.length > 0 && (
              <div className="mb-3">
                <Badge variant="outline" className="text-blue-600">
                  <ImageIcon className="h-3 w-3 mr-1" />
                  {surgery.document_images.length} document{surgery.document_images.length > 1 ? 's' : ''}
                </Badge>
              </div>
            )}

            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={() => handleDelete(surgery.id)} className="text-red-600 hover:text-red-700">
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AddSurgeryDialog open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />
    </>
  );
};

export default SurgeriesTab;